import { useEffect, useMemo } from 'react'

import { PlanTier } from '@/actions/settings/billingActions'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import {
  centsToCurrency,
  formatCurrency,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { useLoadedFlagValue, useReselector } from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useAppDispatch } from '@/utils/typeHelpers'
import { cadencePriceView, cadenceProductIdFor } from './catalogHelpers'
import {
  planCardTitle,
  tierDisplayNamesFor,
  useViewerPlanSegment,
} from '@/features/user/settings/sections/plan-change/lib/helpers'
import { Badge, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { DetailsAccordion } from './DetailsAccordion'
import { selectHasAnnualCommitment } from '@/features/user/settings/sections/self-serve-cancellation/lib/userCancellation.selectors'
import type { ProductFeatureId } from '@/actions/permissionActions'
import { fetchAllAnnualTaxDetailsIfNeeded } from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { useAnnualCommitmentPrice } from './hooks'
import { DateTime } from 'luxon'
import { fetchFreeTrialLength } from '../helpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectUserIsLateJoiner } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { selectProductDescription } from '@/features/user/signup/selectors'
import { usePayrollSignupPromo } from '../payroll-signup-promo.helpers'
import { isPremiumTier } from '../plans/signupPlans'
import { DedicatedExpertSupport } from '../choose-plan/DedicatedExpertSupport'
import {
  SIGNUP_CADENCES,
  type SignupCadence,
  type SignupCard,
} from '../plans/signupPlans.types'

// Lite mirrors the practice-journey Lite card exactly. Essential/Premium
// inherit every Lite section ("Includes: Everything in Lite Plan") and add
// their own tier-specific sections on top.
const LITE_SECTIONS = [
  'bookkeeping' as const,
  'reporting' as const,
  'taxes' as const,
  'support' as const,
]
const STANDARD_SECTIONS = [
  'bookkeeping' as const,
  'reporting' as const,
  'taxes' as const,
  'scorp' as const,
  'group' as const,
  'support' as const,
]

export const CatalogPlanDetailsSection = ({
  plan,
  cadence,
  freeTrialEnabled,
  freeTrialSearchParamValue,
  monthlySavingsOnAnnualPlan,
  productPermissions,
}: {
  plan: SignupCard
  cadence: SignupCadence
  freeTrialEnabled: boolean
  freeTrialSearchParamValue: string | null
  monthlySavingsOnAnnualPlan: string | null
  productPermissions?: Record<string, ProductFeatureId[]>
}) => {
  const dispatch = useAppDispatch()

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const selectedPrice = cadencePriceView(plan, cadence)
  const monthlyPrice = cadencePriceView(plan, SIGNUP_CADENCES.month)
  const isAnnual = cadence === SIGNUP_CADENCES.year
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)
  const { priceAsCurrency } = useAnnualCommitmentPrice()
  const cadenceProductId = cadenceProductIdFor(plan, cadence)
  const isLateJoiner = useReselector(selectUserIsLateJoiner)

  const monthlyDollarsFormatted = useMemo(() => {
    if (selectedPrice && isAnnual) {
      return formatCurrency(
        centsToCurrency(selectedPrice.unitAmountInCents).divide(12),
        { hideDecimalsIfZero: true }
      )
    }
    const productPrice = priceAsCurrency(selectedPrice)
    return formatCurrency(productPrice, {
      hideDecimalsIfZero: true,
    })
  }, [selectedPrice, isAnnual, priceAsCurrency])

  const isLiteProduct = plan.tier === PlanTier.lite
  const { payrollPromoTextForTier } = usePayrollSignupPromo()
  const copyFromTier = useLoadedFlagValue(
    FEATURE_FLAG_KEYS.planCopyFromTier,
    false
  )
  const viewerPlanSegment = useViewerPlanSegment()
  const productDescription = useReselector(selectProductDescription, {
    sections: isLiteProduct ? LITE_SECTIONS : STANDARD_SECTIONS,
    permissions: productPermissions?.[cadenceProductId] ?? [],
    tier: plan.tier,
    copyFromTier: Boolean(copyFromTier),
    isLitePlanEnabled: true,
    payrollPromoText: payrollPromoTextForTier(plan.tier),
  })

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const annualPrice = useMemo(
    () =>
      selectedPrice &&
      formatCurrencyFromCents(selectedPrice.unitAmountInCents, {
        hideDecimalsIfZero: true,
      }),
    [selectedPrice]
  )

  const freeTrialDays = fetchFreeTrialLength(freeTrialSearchParamValue)
  const trialEndDate = DateTime.now()
    .plus({ days: freeTrialDays })
    .toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  const showFreeTrialAnnualVersion =
    freeTrialEnabled && isAnnual && !hasAnnualCommitment

  return (
    <div className="flex flex-col">
      <div className="flex mb-4">
        <Text
          className="w-1/2"
          color={TextColors.MUTED}
          variant={TypographyStyles.EYEBROW}
        >
          {isAnnual ? 'annual plan' : 'monthly plan'}
        </Text>

        <Text
          className="w-1/2 text-right"
          color={TextColors.MUTED}
          variant={TypographyStyles.EYEBROW}
        >
          {!isAnnual &&
            !isLateJoiner &&
            !hasAnnualCommitment &&
            'cancel anytime'}
        </Text>
      </div>

      <div className="flex mb-2">
        <Text
          className="w-[56.25%]"
          variant={isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM}
        >
          {planCardTitle(
            plan.tier,
            plan.displayName,
            tierDisplayNamesFor(viewerPlanSegment)
          )}
        </Text>

        <div className="w-[43.75%] text-right relative">
          {showFreeTrialAnnualVersion && monthlySavingsOnAnnualPlan && (
            <Badge color="green" variant="secondary" className="mr-2">
              Save {monthlySavingsOnAnnualPlan}/mo!
            </Badge>
          )}

          <Text
            className="inline"
            variant={
              isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM
            }
          >
            {showFreeTrialAnnualVersion ? annualPrice : monthlyDollarsFormatted}
          </Text>

          <Text
            className="inline"
            variant={
              isMobile ? TypographyStyles.H3 : TypographyStyles.DISPLAY_XXS
            }
          >
            {showFreeTrialAnnualVersion ? '/yr' : '/mo'}
          </Text>

          <div className="flex flex-col items-end gap-0.5 mt-0.5">
            {hasAnnualCommitment && !isAnnual && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_MD}
                className="line-through"
                textAlign="right"
              >
                {formatCurrencyFromCents(selectedPrice?.unitAmountInCents, {
                  hideDecimalsIfZero: true,
                })}{' '}
                /mo
              </Text>
            )}
            {isAnnual && monthlyPrice?.unitAmountInCents && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_MD}
                className="line-through"
                textAlign="right"
              >
                {formatCurrencyFromCents(monthlyPrice.unitAmountInCents, {
                  hideDecimalsIfZero: true,
                })}{' '}
                /mo
              </Text>
            )}

            {isAnnual && (
              <Text
                color={TextColors.MUTED}
                variant={TypographyStyles.BODY_XS}
                textAlign="right"
              >
                {freeTrialEnabled
                  ? `Billed annually starting ${trialEndDate}`
                  : `Billed ${annualPrice} annually`}
              </Text>
            )}
          </div>
        </div>
      </div>

      {isPremiumTier(plan.tier) && (
        <div className="mb-2">
          <DedicatedExpertSupport isTrial={freeTrialEnabled} />
        </div>
      )}

      <div className="min-h-fit">
        <DetailsAccordion productDescription={productDescription} />
      </div>
    </div>
  )
}
